import { ContractPackagePriceResponseMessage } from '../../../common/api/clients/packagePrice.api';
import { formatMoney } from '../../../common/helpers/formatMoney.helper';
import { translate } from '../../../common/helpers/translate.helper';
import { SupplierSelectionPriceState } from '../../create-enquiry/components/SupplierSelection.types';
import {
    SupplierSelectionPriceData,
    SupplierSelectionPriceItem,
    TranslatedItemsBySupplier,
} from '../../create-enquiry/hooks/useSupplierSelectionPrices.types';

interface ContractSupplierSelectionPriceTarget {
    contractId: string;
    supplierId: string;
}

interface MapContractSupplierSelectionPricesProps {
    targets: ContractSupplierSelectionPriceTarget[];
    translatedItemsBySupplier: TranslatedItemsBySupplier[];
    shouldFetchPrices: boolean;
    products: Array<{ id: string; name?: string }>;
    isProductsPending: boolean;
    isProductsError: boolean;
    contractPrices: ContractPackagePriceResponseMessage[];
    isContractPricesPending: boolean;
    isContractPricesError: boolean;
}

export const mapContractSupplierSelectionPrices = ({
    targets,
    translatedItemsBySupplier,
    shouldFetchPrices,
    products,
    isProductsPending,
    isProductsError,
    contractPrices,
    isContractPricesPending,
    isContractPricesError,
}: MapContractSupplierSelectionPricesProps): Record<string, SupplierSelectionPriceData> => {
    const supplierItemsBySupplierId = new Map(
        translatedItemsBySupplier.map((translatedItems) => [translatedItems.supplier.id, translatedItems.items])
    );
    const productsById = new Map(products.map((product) => [product.id, product]));
    const pricesByKey = new Map(
        contractPrices.flatMap((price) => {
            if (!price.contractId) {
                return [];
            }

            return [[`${price.contractId}:${price.productId}:${price.packType}`, price.price] as const];
        })
    );

    return Object.fromEntries(
        targets.map((target) => {
            if (!shouldFetchPrices) {
                return [
                    target.contractId,
                    { state: SupplierSelectionPriceState.HIDDEN } satisfies SupplierSelectionPriceData,
                ];
            }

            if (isContractPricesPending || isProductsPending) {
                return [
                    target.contractId,
                    { state: SupplierSelectionPriceState.LOADING } satisfies SupplierSelectionPriceData,
                ];
            }

            if (isContractPricesError || isProductsError) {
                return [
                    target.contractId,
                    { state: SupplierSelectionPriceState.HIDDEN } satisfies SupplierSelectionPriceData,
                ];
            }

            const supplierItems = supplierItemsBySupplierId.get(target.supplierId);
            const itemsWithPrices =
                supplierItems?.reduce<SupplierSelectionPriceItem[]>((acc, item) => {
                    if (!item.translatedProduct) {
                        return acc;
                    }

                    const productId = item.translatedProduct.productId;
                    const baseProductId = item.translatedProduct.baseProductId;
                    const unitPrice = pricesByKey.get(`${target.contractId}:${productId}:${item.packType}`);

                    acc.push({
                        productId,
                        isTranslated: item.translatedProduct.isTranslated,
                        isTranslationSuccessful: item.translatedProduct.isSuccessful,
                        productName: productsById.get(productId)?.name,
                        baseProductName: productsById.get(baseProductId)?.name || '',
                        unitPrice: unitPrice ?? undefined,
                        totalPrice: unitPrice
                            ? {
                                  value: unitPrice.value * (item.volume || 0),
                                  currency: unitPrice.currency,
                              }
                            : undefined,
                    });

                    return acc;
                }, []) || [];

            if (!itemsWithPrices.length) {
                return [
                    target.contractId,
                    { state: SupplierSelectionPriceState.HIDDEN } satisfies SupplierSelectionPriceData,
                ];
            }

            if (itemsWithPrices.some((item) => !item.unitPrice)) {
                return [
                    target.contractId,
                    {
                        state: SupplierSelectionPriceState.UNAVAILABLE,
                        label: translate('global.notavailable'),
                        items: itemsWithPrices,
                    } satisfies SupplierSelectionPriceData,
                ];
            }

            const totalPrice = itemsWithPrices.reduce((acc, item) => acc + (item.totalPrice?.value || 0), 0);
            const contractPrice = contractPrices.find((price) => price.contractId === target.contractId);

            return [
                target.contractId,
                {
                    state: SupplierSelectionPriceState.AVAILABLE,
                    totalPrice: {
                        value: totalPrice,
                        currency: itemsWithPrices[0]?.totalPrice?.currency || 'USD',
                    },
                    label: formatMoney({
                        value: totalPrice,
                        currency: itemsWithPrices[0]?.totalPrice?.currency || 'USD',
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                    }),
                    items: itemsWithPrices,
                    isOutdated: contractPrice?.isOutdated ?? false,
                    validUntil: contractPrice?.validUntil ?? undefined,
                } satisfies SupplierSelectionPriceData,
            ];
        })
    ) as Record<string, SupplierSelectionPriceData>;
};
